if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (!button) return;

              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                  false
                )
              );
            });

          if (
            this.dataset.desktopLayout.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const mediaId =
          event.detail.currentElement?.dataset?.mediaId;

        if (!mediaId) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(
        mediaId,
        prepend = false,
        variantChange = false
      ) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) return;

        /*
         * IMPORTANTE:
         * No hacemos prepend.
         *
         * La imagen conserva siempre su posición real
         * dentro del carrusel.
         */
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        this.preventStickyHeader();

        const viewerSlider =
          this.elements.viewer.slider ||
          this.elements.viewer.querySelector(
            '[id^="Slider-Gallery"]'
          ) ||
          activeMedia.parentElement;

        /*
         * Movemos el carrusel principal hasta la posición
         * REAL de la imagen.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (viewerSlider) {
              viewerSlider.scrollTo({
                left: activeMedia.offsetLeft,
                behavior: variantChange
                  ? 'smooth'
                  : 'auto',
              });
            }

            if (
              typeof this.elements.viewer.resetPages ===
              'function'
            ) {
              this.elements.viewer.resetPages();
            }

            /*
             * Movemos también el carrusel de miniaturas
             * hasta la misma imagen.
             */
            if (this.elements.thumbnails) {
              const activeThumbnail =
                this.elements.thumbnails.querySelector(
                  `[data-target="${mediaId}"]`
                );

              this.setActiveThumbnail(
                activeThumbnail,
                variantChange
              );

              if (
                activeThumbnail &&
                this.elements.liveRegion
              ) {
                this.announceLiveRegion(
                  activeMedia,
                  activeThumbnail.dataset.mediaPosition
                );
              }
            }
          });
        });

        /*
         * Si la galería estaba fuera de la parte visible
         * de la página, conservamos el comportamiento
         * normal de Dawn.
         */
        window.setTimeout(() => {
          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);
      }

      setActiveThumbnail(
        thumbnail,
        forceScroll = false
      ) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute(
            'aria-current',
            'true'
          );
        }

        const thumbnailSlider =
          this.elements.thumbnails.slider ||
          this.elements.thumbnails.querySelector(
            '[id^="Slider-Thumbnails"]'
          );

        if (!thumbnailSlider) return;

        if (
          !forceScroll &&
          typeof this.elements.thumbnails
            .isSlideVisible === 'function' &&
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        /*
         * Calculamos la posición dentro de la tira.
         * No movemos la miniatura en el DOM:
         * movemos el scroll hasta ella.
         */
        const maxScroll = Math.max(
          0,
          thumbnailSlider.scrollWidth -
            thumbnailSlider.clientWidth
        );

        const targetLeft = Math.min(
          Math.max(thumbnail.offsetLeft, 0),
          maxScroll
        );

        thumbnailSlider.scrollTo({
          left: targetLeft,
          behavior: 'smooth',
        });

        if (
          typeof this.elements.thumbnails.resetPages ===
          'function'
        ) {
          this.elements.thumbnails.resetPages();
        }
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (
          !image ||
          !this.elements.liveRegion
        ) {
          return;
        }

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (
          !this.elements.viewer ||
          !this.elements.viewer.slider
        ) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}